import { QueueItem, QueueItemType } from "@/api/deduction.tsx"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { DeductionListNextActionCell } from "@/deduction_next_action/deduction_next_action_list"
import { CategoryCell } from "@/deduction_table/CategoryCell"
import { api_fetch } from "@/api/client"
import { fetchQueueItems, inboxState, setActiveFilters, startPolling, stopPolling } from "@/stores/inbox"
import { classes } from "@/utils"
import { formatDistanceToNow } from "date-fns"
import {
  CheckCircle2,
  Clock,
  ExternalLinkIcon,
  Loader2,
  Mail,
  AlertCircle,
  FileText,
  MessageSquare,
  Inbox,
} from "lucide-react"
import { useEffect } from "preact/hooks"
import { openDeductionDetailModal } from "@/deduction/deduction_detail_modal_state"

interface QueueListProps {
  isStandalone?: boolean
  onClose?: () => void
}

const FILTER_OPTIONS: Array<{ value: QueueItemType[]; label: string }> = [
  { value: [], label: "All" },
  { value: ["external_message", "internal_message"], label: "Messages" },
  { value: ["completed_task"], label: "Tasks" },
  { value: ["expiring_deduction"], label: "Expiring" },
  { value: ["disputable_deduction"], label: "Disputable" },
]

function getItemIcon(itemType: QueueItemType) {
  switch (itemType) {
    case "external_message":
      return Mail
    case "internal_message":
      return MessageSquare
    case "completed_task":
      return CheckCircle2
    case "expiring_deduction":
      return Clock
    case "disputable_deduction":
      return AlertCircle
    default:
      return FileText
  }
}

function getItemTypePill(itemType: QueueItemType, statusValue?: string) {
  switch (itemType) {
    case "external_message":
      return { label: "Message", className: "bg-blue-100 text-blue-800 border-blue-300" }
    case "internal_message":
      return { label: "Note", className: "bg-gray-100 text-gray-800 border-gray-300" }
    case "completed_task":
      return { label: "Task", className: "bg-green-100 text-green-800 border-green-300" }
    case "expiring_deduction":
      return { label: "Expiring", className: "bg-orange-100 text-orange-800 border-orange-300" }
    case "disputable_deduction":
      const label = statusValue === "disputable" ? "Disputable" : "Review"
      return { label, className: "bg-purple-100 text-purple-800 border-purple-300" }
    default:
      return { label: "Other", className: "bg-gray-100 text-gray-800 border-gray-300" }
  }
}

interface QueueItemComponentProps {
  item: QueueItem
  onClose?: () => void
}

function QueueItemComponent({ item, onClose }: QueueItemComponentProps) {
  const Icon = getItemIcon(item.item_type)
  const formattedDate = item.last_touched_at
    ? formatDistanceToNow(new Date(item.last_touched_at), { addSuffix: true })
    : formatDistanceToNow(new Date(item.created_at), { addSuffix: true })

  const handleView = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (onClose) {
      onClose()
    }
    openDeductionDetailModal(item.deduction_id)
  }

  const handleOpenInNewTab = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    window.open(`/deduction/${item.deduction_id}`, "_blank")
  }

  const handleMarkRead = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (item.message_id) {
      const result = await api_fetch<void>(`/message/${item.message_id}/read`, {
        method: "POST",
        body: {},
      })
      if (result.ok) {
        await fetchQueueItems()
      }
    } else if (item.note_id) {
      const result = await api_fetch<void>(`/note/${item.note_id}/read`, {
        method: "POST",
        body: {},
      })
      if (result.ok) {
        await fetchQueueItems()
      }
    }
  }

  const handleMarkUnread = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (item.message_id) {
      const result = await api_fetch<void>(`/message/${item.message_id}/unread`, {
        method: "POST",
        body: {},
      })
      if (result.ok) {
        await fetchQueueItems()
      }
    } else if (item.note_id) {
      const result = await api_fetch<void>(`/note/${item.note_id}/unread`, {
        method: "POST",
        body: {},
      })
      if (result.ok) {
        await fetchQueueItems()
      }
    }
  }

  const handleValidate = async (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const result = await api_fetch<void>("/statuses", {
      method: "POST",
      body: [
        {
          deduction_id: item.deduction_id,
          status: "validated",
        },
      ],
    })

    if (result.ok) {
      await fetchQueueItems()
    }
  }

  // Determine read status based on message or note read_at
  const isUnread =
    item.item_type === "external_message" || item.item_type === "internal_message"
      ? item.item_type === "external_message"
        ? !item.message_read_at
        : !item.note_read_at
      : false

  return (
    <>
      <div
        className={classes(
          "flex mt-2 gap-2 items-start ml-2 px-3 py-2 rounded-md transition-colors",
          "hover:bg-muted/50",
          isUnread && "bg-blue-100 border-l-4 border-blue-500 shadow-sm"
        )}>
        <div className="flex flex-col items-center gap-2">
          <div
            className={classes(
              "inline-flex items-center justify-center border p-2 rounded-3xl",
              isUnread ? "border-blue-500 bg-blue-100 shadow-sm" : "border-gray-300 bg-gray-50"
            )}>
            <Icon className={classes(isUnread ? "h-6 w-6 text-blue-700" : "h-5 w-5 text-gray-600")} />
          </div>
          {(() => {
            const pill = getItemTypePill(item.item_type, item.deduction.status_value)
            return <span className={classes("text-xs px-2 py-0.5 rounded border", pill.className)}>{pill.label}</span>
          })()}
        </div>
        <div className="flex flex-col grow overflow-hidden">
          <div className="flex justify-between w-full items-start">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <div className={classes("text-md", isUnread ? "font-bold text-gray-900" : "font-medium")}>
                  {item.title}
                </div>
                <CategoryCell deduction={item.deduction} />
              </div>
              <div className="text-sm text-muted-foreground">
                Inv #: {item.deduction_invoice_number} • ${item.deduction_amount.toLocaleString()}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="text-gray-500 text-sm">{formattedDate}</div>
              <ExternalLinkIcon className="h-4 w-4 cursor-pointer text-black underline" onClick={handleOpenInNewTab} />
            </div>
          </div>
          <div className={classes("text-sm mt-1", isUnread ? "text-gray-800 font-semibold" : "text-muted-foreground")}>
            {item.description}
          </div>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            {item.action && <DeductionListNextActionCell deduction={item.deduction} />}
            <Button variant="outline" size="sm" onClick={handleView}>
              View
            </Button>
            {(item.item_type === "external_message" || item.item_type === "internal_message") && (
              <>
                {isUnread ? (
                  <Button variant="outline" size="sm" onClick={handleMarkRead}>
                    Mark Read
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" onClick={handleMarkUnread}>
                    Mark Unread
                  </Button>
                )}
              </>
            )}
            {item.item_type === "expiring_deduction" || item.item_type === "disputable_deduction" ? (
              <Button variant="outline" size="sm" onClick={handleValidate}>
                Validate
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <div className="px-4 w-full flex justify-center">
        <Separator />
      </div>
    </>
  )
}

export function QueueList({ isStandalone, onClose }: QueueListProps) {
  const { queueItems, isLoading } = inboxState.use()
  const activeFilters = inboxState.use(state => state.activeFilters)

  useEffect(() => {
    fetchQueueItems()
    startPolling()
    return () => stopPolling()
  }, [])

  // Filter items based on active filters
  const items = queueItems || []
  const filters = activeFilters || []
  const filteredItems = filters.length === 0 ? items : items.filter(item => filters.includes(item.item_type))

  if (isLoading && (queueItems || []).length === 0) {
    return (
      <div className="flex justify-center items-center h-[200px]">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Filter Buttons */}
      <div className="flex gap-2 p-4 border-b flex-wrap">
        {FILTER_OPTIONS.map(option => {
          const currentFilters = filters
          const isActive =
            option.value.length === 0
              ? currentFilters.length === 0
              : option.value.every(v => currentFilters.includes(v)) &&
                currentFilters.every(v => option.value.includes(v))

          return (
            <Button
              key={option.label}
              variant={isActive ? "default" : "outline"}
              size="sm"
              onClick={() => setActiveFilters(option.value)}>
              {option.label}
            </Button>
          )
        })}
      </div>

      {/* Queue Items */}
      <div className="overflow-auto max-h-[calc(100vh-200px)]">
        {filteredItems.length === 0 ? (
          <div className="p-6 text-center text-muted-foreground">No items in queue</div>
        ) : (
          filteredItems.map(item => (
            <QueueItemComponent
              key={`${item.item_type}-${item.deduction_id}-${item.message_id || item.note_id || item.task_id || ""}`}
              item={item}
              onClose={onClose}
            />
          ))
        )}

        {isLoading && (
          <div className="flex justify-center p-4">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        )}
      </div>
    </div>
  )
}
